import React, { FC, useEffect, useState } from 'react';

import { Modal } from '../../../components/modal/index';
import {
  HolidayRegion,
  HolidayRegionFlag,
  HolidayRegionTitle,
  PublicHoliday,
} from '../../../types/team.types';
import { getFieldErrors, holidayFormSchema, HolidayFormValues } from '../team.validation';
import { DateField, SelectField, TextField } from '../team-field';

export type HolidayFormModalProps = {
  holiday: null | PublicHoliday;
  isOpen: boolean;
  isPending: boolean;
  onDelete?: () => void;
  onDismiss: () => void;
  onSubmit: (values: HolidayFormValues) => void;
};

const emptyValues: HolidayFormValues = {
  name: '',
  date: '',
  region: HolidayRegion.UKRAINE,
};

const regionOptions = Object.values(HolidayRegion).map((region) => ({
  label: `${HolidayRegionFlag[region]} ${HolidayRegionTitle[region]}`,
  value: region,
}));

export const HolidayFormModal: FC<HolidayFormModalProps> = ({
  isOpen,
  isPending,
  holiday,
  onDelete,
  onDismiss,
  onSubmit,
}) => {
  const [values, setValues] = useState<HolidayFormValues>(emptyValues);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!isOpen) return;

    setErrors({});
    setValues(
      holiday ? { name: holiday.name, date: holiday.date, region: holiday.region } : emptyValues,
    );
  }, [isOpen, holiday]);

  const handleSave = () => {
    const result = holidayFormSchema.safeParse(values);

    if (!result.success) {
      setErrors(getFieldErrors(result.error));
      return;
    }

    onSubmit(result.data);
  };

  return (
    <Modal
      className="max-w-[440px]"
      crossButton
      dismissButtonContent="Cancel"
      isOpen={isOpen}
      onDismiss={onDismiss}
      onHardClose={onDismiss}
      onSuccess={isPending ? undefined : handleSave}
      successButtonClassName="bg-brand-700"
      successButtonContent={isPending ? 'Saving…' : holiday ? 'Save changes' : 'Add holiday'}
      title={holiday ? 'Edit public holiday' : 'Add public holiday'}
    >
      <div className="flex flex-col gap-4">
        <TextField
          error={errors.name}
          label="Holiday name"
          onChange={(value) => setValues((prev) => ({ ...prev, name: value }))}
          placeholder="e.g. Independence Day"
          value={values.name}
        />
        <DateField
          error={errors.date}
          label="Date"
          onChange={(value) => setValues((prev) => ({ ...prev, date: value }))}
          value={values.date}
        />
        <SelectField
          error={errors.region}
          label="Region"
          onChange={(value) => setValues((prev) => ({ ...prev, region: value as HolidayRegion }))}
          options={regionOptions}
          value={values.region}
        />
        {holiday && onDelete && (
          <button
            className="self-start text-xs font-[550] text-fireside-600 hover:underline"
            onClick={onDelete}
            type="button"
          >
            Delete this holiday
          </button>
        )}
      </div>
    </Modal>
  );
};
